import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { StandardIds } from '@galaxy/crm/static';
import { FieldValueInterface } from '@vendasta/crm';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { BlankValueComponent, CustomCellComponent } from '@vendasta/galaxy/table';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { PartnerCRMSalespersonService, SalespersonWithDisplayName } from '../../salesperson.service';

const NO_VALUE_FOR_SALESPERSON = '<NO_VALUE_FOR_SALESPERSON>';

@Component({
  template: `
    <ng-container *ngIf="salespeople() as salespeople; else loading">
      <div class="salesperson" *ngIf="salespeople !== NO_VALUE_FOR_SALESPERSON; else blank">
        <div class="tags">
          <glxy-badge [glxyTooltip]="salespeopleNames().join(', ')">{{ salespeopleNames()[0] }}</glxy-badge>
          <glxy-badge *ngIf="salespeopleNames().length === 2" [glxyTooltip]="salespeopleNames().join(', ')">
            {{ salespeopleNames()[1] }}
          </glxy-badge>
          <glxy-badge *ngIf="salespeopleNames().length > 2" [glxyTooltip]="salespeopleNames().join(', ')">
            +{{ salespeopleNames().length - 1 }}
          </glxy-badge>
        </div>
      </div>
    </ng-container>

    <ng-template #loading>
      <div class="stencil-shimmer"></div>
    </ng-template>
    <ng-template #blank>
      <glxy-blank-value></glxy-blank-value>
    </ng-template>
  `,
  standalone: true,
  styleUrls: ['./additional-salespeople.component.scss'],
  imports: [CommonModule, BlankValueComponent, GalaxyBadgeModule, GalaxyTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdditionalSalespeopleCellComponent extends CustomCellComponent {
  NO_VALUE_FOR_SALESPERSON = NO_VALUE_FOR_SALESPERSON;
  fieldValues = signal([]);
  set value(newValues: (FieldValueInterface | null)[]) {
    this.fieldValues.set(newValues);
  }
  salespeople: WritableSignal<SalespersonWithDisplayName[] | '<NO_VALUE_FOR_SALESPERSON>' | null> = signal(null);
  salespeopleNames = computed(() => {
    const salespeople = this.salespeople();
    if (salespeople === NO_VALUE_FOR_SALESPERSON) {
      return [];
    }
    return salespeople.map((salesperson) => salesperson.displayName);
  });

  constructor(private readonly salespersonService: PartnerCRMSalespersonService) {
    super();
    toObservable(this.fieldValues)
      .pipe(
        switchMap((fieldValues) => {
          const salespersonFieldValue =
            fieldValues.find(
              (fieldValue) =>
                fieldValue?.fieldId === StandardIds.CompanyAdditionalSalespersonIDs ||
                fieldValue?.fieldId === StandardIds.ContactAdditionalSalespersonIDs,
            ) ?? null;
          if (!salespersonFieldValue) {
            return of(NO_VALUE_FOR_SALESPERSON);
          }
          const salespersonIds = salespersonFieldValue.stringValues?.values ?? null;
          if (!salespersonIds.length) {
            return of(NO_VALUE_FOR_SALESPERSON);
          }
          return this.salespersonService.getMultiSalesperson(salespersonIds);
        }),
        takeUntilDestroyed(),
      )
      .subscribe((result) => {
        if (result === NO_VALUE_FOR_SALESPERSON) {
          this.salespeople.set(NO_VALUE_FOR_SALESPERSON);
          return;
        }
        const salespersons = result as SalespersonWithDisplayName[];
        const filteredNonExisting = salespersons.filter((s) => !!s?.salespersonId);
        if (!filteredNonExisting.length) {
          this.salespeople.set(NO_VALUE_FOR_SALESPERSON);
          return;
        }
        this.salespeople.set(filteredNonExisting);
      });
  }
}
